import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import useAuth from './useAuth';
import './Manage_User.css';
import * as XLSX from "xlsx-js-style";
import Header from './Header.js';
import Sidebar from './Sidebar.js';
import Footer from './Footer.js';
import Pagination from './Pagination.js';
import RejectedListModal from './SettlementRejectedList.js';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Loader = () => (
    <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
        </div>
        <p>Loading...</p>
    </div>
);

const AuditForm = () => {
    const [EmpCode, setEmpCode] = useState('');

    const [RptMgr, setRptMgr] = useState('');
    const [items, setItems] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [responseMessage, setResponseMessage] = useState('');
    const [showRejectedModal, setShowRejectedModal] = useState(false);
    const [showLogPopup, setShowLogPopup] = useState(false);
    const [logResponse, setLogResponse] = useState(null);
    const [filteredItems, setFilteredItems] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [is_audit_approval, setis_audit_approval] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(100);
    const [sortColumn, setSortColumn] = useState('SysNo');
    const [sortOrder, setSortOrder] = useState('desc');
    const navigate = useNavigate();

    useAuth();



    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch(`${API_BASE_URL}profile`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const result = await response.json();

                if (result.status) {
                    setEmpCode(result.profile.emp_code || '');
                    setRptMgr(result.profile.report_mngr || '');
                    setis_audit_approval(result.profile.is_audit_approval === 'Y');
                } else {
                    setResponseMessage('Error: ' + result.message);
                }
            } catch (error) {
                console.error('Fetch error:', error);
                setResponseMessage('Error: ' + error.message);
            }
        };

        fetchProfileData();
    }, []);

    // Fetch items based on EmpCode and Audit Status Filter
    useEffect(() => {
        const fetchItems = async () => {
            try {
                const token = localStorage.getItem('authToken');
                if (!token || !EmpCode) return;

                let url = `${API_BASE_URL}getsettlement_check?audit_manager=${encodeURIComponent(EmpCode)}`;



                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const result = await response.json();
                if (result.status) {
                    setItems(result.settlement);
                } else {
                    setError(result.message);
                }
            } catch (error) {
                console.error('Fetch error:', error);
                setError('Failed to fetch items');
            } finally {
                setLoading(false);
            }
        };

        fetchItems();
    }, [EmpCode]); // Re-fetch when EmpCode or auditStatusFilter changes

    const handleLog = async (id) => {
        try {
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${API_BASE_URL}settlement_log_form/${id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json();

            // Save API response
            setLogResponse(result);
            setShowLogPopup(true);

        } catch (error) {
            console.error("Error fetching log:", error);
            setLogResponse({ message: "Something went wrong!" });
            setShowLogPopup(true);
        }
    };

    useEffect(() => {
        const filtered = (items || []).filter(item => {

            // ✅ ONLY APPROVED
            const approvedMatch = item.audit_status === "Approved";

            // ✅ SEARCH
            const search = searchQuery.toLowerCase();
            const searchMatch =
                !search ||
                (item.emp_code || '').toLowerCase().includes(search);

            // ✅ DATE
            const itemAuditDate = item.audit_approved_date
                ? new Date(item.audit_approved_date)
                : null;

            // ✅ SINGLE DATE FILTER 🔥
            const selectedMatch = (() => {
                if (!selectedDate) return true;

                if (!item.audit_approved_date) return false;

                const d = new Date(item.audit_approved_date);

                if (isNaN(d.getTime())) return false;

                return d.toLocaleDateString('en-CA') === selectedDate;
            })();

            return approvedMatch && searchMatch && selectedMatch;
        });

        const sorted = [...filtered].sort((a, b) => {
            if (a[sortColumn] < b[sortColumn]) return sortOrder === 'asc' ? -1 : 1;
            if (a[sortColumn] > b[sortColumn]) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        setFilteredItems(sorted);
        setCurrentPage(1);

    }, [searchQuery, items, sortColumn, sortOrder, selectedDate]);

    // Pagination logic
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

    const startItem = indexOfFirstItem + 1;
    const endItem = Math.min(indexOfLastItem, filteredItems.length);



    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };
    const parseRemarks = (remarks) => {
        try {
            const jsonStart = remarks.indexOf('{');
            if (jsonStart === -1) return null;
            return JSON.parse(remarks.substring(jsonStart));
        } catch (e) {
            return null;
        }
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleSort = (column) => {
        let sortedItems;
        if (column === 'sno') {
            sortedItems = [...items].sort((a, b) => {
                const indexA = items.indexOf(a);
                const indexB = items.indexOf(b);
                return sortOrder === 'asc' ? indexA - indexB : indexB - indexA;
            });
        } else {
            sortedItems = [...items].sort((a, b) => {
                if (a[column] < b[column]) return sortOrder === 'asc' ? -1 : 1;
                if (a[column] > b[column]) return sortOrder === 'asc' ? 1 : -1;
                return 0;
            });
        }

        setItems(sortedItems);
        setSortColumn(column);
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    };

    const getEmpRequestedAmount = (item) => {
        const userAmt = Math.trunc(Number(item.grandTotalAmount_user || 0));
        return userAmt === 0
            ? Math.trunc(Number(item.TotalAmount || 0))
            : userAmt;
    };
    const exportToExcel = () => {
        const data = filteredItems.map(item => ({
            "Verified by BMID": item.audit_manager,
            "RM Approval Date": item.manager_approved_date,
            "Emp Id": item.emp_code,
            "Employee Name": item.comp_name,
            "Start date": new Date(item.from_date).toLocaleDateString("en-GB").replace(/\//g, "-"),
            "End date": new Date(item.to_date).toLocaleDateString("en-GB").replace(/\//g, "-"),
            "Band": item.grade,
            "Designation": item.designation_name,
            "Department": item.dept_name,
            "From Location": item.from_city_name,
            "To Location": item.to_city_name,
            "Emp Requested Amount": getEmpRequestedAmount(item),
            "Validated Amount": Math.trunc(item.audit_approved_amount),
            "AuditApprovedDate": item.audit_approved_date,
            "Lodge Booked": item.hotel_booked_by,
            "Travel Booked": item.ticket_travel_booked_by,
            "Audit Remarks": item.audit_remarks,
        }));

        const worksheet = XLSX.utils.json_to_sheet(data);

        // ✅ Apply yellow background to header row
        const range = XLSX.utils.decode_range(worksheet['!ref']);

        for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });

            if (!worksheet[cellAddress]) continue;

            worksheet[cellAddress].s = {
                fill: {
                    fgColor: { rgb: "FFFF00" } // 🟡 Yellow
                },
                font: {
                    bold: true
                }
            };
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Settlement");

        XLSX.writeFile(workbook, "Settlement_Details.xlsx");
    };

    const overlayStyle = {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999
    };

    const modalStyle = {
        background: "#fff",
        padding: "20px",
        borderRadius: "8px",
        width: "600px",
        maxHeight: "80vh",
        overflowY: "auto"
    };

    return (
        <div className="hold-transition sidebar-mini layout-fixed" style={{ fontSize: '12px' }}>
            <div className="wrapper">
                <Header />
                <Sidebar />
                <div className="content-wrapper">
                    <div className="content-header">
                        <div className="container-fluid">
                            <div className="row mb-2">
                                <div className="col-sm-6">
                                    <h4 className="m-0" style={{ fontSize: '20px' }}>Tour/Travel Settlement Audit Approval Form</h4>
                                </div>
                                <div className="col-sm-6">
                                    <ol className="breadcrumb float-sm-right">
                                        <li className="breadcrumb-item" style={{ fontSize: '12px' }}><a href="/dashboard">Home</a></li>

                                        <li className="breadcrumb-item active" style={{ fontSize: '12px' }}>Tour/Travel Settlement Audit Approval Form</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                    {showRejectedModal && (
                        <RejectedListModal onClose={() => setShowRejectedModal(false)} />
                    )}
                    {loading ? (
                        <Loader />
                    ) : (
                        <>

                            <section className="content">
                                <div className="container-fluid">
                                    <div className="row">
                                        <section className="col-lg-12 connectedSortable">
                                            <div className="card">
                                                <div className="card-header">
                                                    <div className="row align-items-end">

                                                        {/* Items Per Page */}
                                                        <div className="col-md-2">
                                                            <label className="mb-1">Show</label>
                                                            <select
                                                                className="form-control form-control-sm"
                                                                value={itemsPerPage}
                                                                onChange={handleItemsPerPageChange}
                                                            >
                                                                <option value={10}>10</option>
                                                                <option value={50}>50</option>
                                                                <option value={100}>100</option>
                                                            </select>
                                                        </div>

                                                        {/* Audit Approved Date */}
                                                        <div className="col-md-3">
                                                            <label className="mb-1">Audit Approved Date</label>
                                                            <input
                                                                type="date"
                                                                className="form-control form-control-sm"
                                                                value={selectedDate}
                                                                onChange={(e) => setSelectedDate(e.target.value)}
                                                            />
                                                        </div>

                                                        {/* Search */}
                                                        <div className="col-md-3">
                                                            <label className="mb-1">Search</label>
                                                            <input
                                                                type="search"
                                                                value={searchQuery}
                                                                onChange={handleSearchChange}
                                                                className="form-control form-control-sm"
                                                                placeholder="Search"
                                                            />
                                                        </div>

                                                        {/* Buttons */}
                                                        <div className="col-md-4 d-flex justify-content-end gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-warning btn-sm"
                                                                onClick={() => {
                                                                    setFromDate('');
                                                                    setToDate('');
                                                                    setSelectedDate('');
                                                                }}
                                                            >
                                                                Reset
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary btn-sm"
                                                                onClick={exportToExcel}
                                                            >
                                                                Export
                                                            </button>
                                                        </div>

                                                    </div>
                                                </div>
                                                <div className="card-body">
                                                    {/* <div id="example1_filter" style={{ float: 'right' }} className="dataTables_filter">
                                                        <label>
                                                            <input
                                                                type="search"
                                                                value={searchQuery}
                                                                onChange={handleSearchChange}
                                                                className="form-control form-control-sm"
                                                                placeholder="Search"
                                                                aria-controls="example1"
                                                            />
                                                        </label>
                                                    </div>
                                                    <div className="form-group">
                                                        <select
                                                            className="form-control custom-select-sm"
                                                            value={itemsPerPage}
                                                            onChange={handleItemsPerPageChange}
                                                        >
                                                            <option value={10}>10</option>
                                                            <option value={50}>50</option>
                                                            <option value={100}>100</option>
                                                        </select>
                                                    </div> */}
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-striped" style={{ fontSize: '13px' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th
                                                                        onClick={() => handleSort("sno")}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            backgroundColor: sortColumn === "sno" ? '#f8f9fa' : 'transparent',
                                                                            position: 'relative'
                                                                        }}
                                                                    >
                                                                        S.No {sortColumn === "sno" && (sortOrder === "asc" ? '▲' : '▼')}
                                                                        {/* Adding arrows for sorting indication */}
                                                                        <span
                                                                            style={{
                                                                                position: 'absolute',
                                                                                right: '10px',
                                                                                color: '#007bff',
                                                                                fontSize: '12px',
                                                                                textDecoration: 'underline',
                                                                            }}
                                                                        >

                                                                        </span>
                                                                    </th>

                                                                    <th>
                                                                        Emp Code
                                                                    </th>
                                                                    <th>
                                                                        Trans Code
                                                                    </th>
                                                                    <th>
                                                                        Emp Name
                                                                    </th>

                                                                    <th>
                                                                        Grade
                                                                    </th>
                                                                    <th>
                                                                        Purpose
                                                                    </th>
                                                                    <th>From Date</th>
                                                                    <th>To Date</th>
                                                                    <th>
                                                                        Employee requested Amount
                                                                    </th>
                                                                    <th>
                                                                        Audit Approved Amount
                                                                    </th>
                                                                    <th>
                                                                        Audit Approved Date
                                                                    </th>

                                                                    {/* <th>
                                                                        Travel Settlement Amount
                                                                    </th> */}


                                                                    {/* <th>
                                                                        Reporting Manager
                                                                    </th> */}
                                                                    <th>
                                                                        Days
                                                                    </th>


                                                                    <th>
                                                                        Reporting Manager Status
                                                                    </th>

                                                                    <th>
                                                                        Audit Status
                                                                    </th>

                                                                    <th>
                                                                        Finance Status
                                                                    </th>

                                                                    {/* <th>Action</th> */}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {is_audit_approval && currentItems.length > 0 ? (
                                                                    currentItems.some(item => item.rpt_mgr_status !== 'Rejected') ? (
                                                                        currentItems.map((item, index) => (

                                                                            <tr key={index}>
                                                                                <td>{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                                                                                <td>{item.emp_code}</td>
                                                                                <td>{item.trans_code}</td>
                                                                                <td>{item.comp_name}</td>
                                                                                <td>{item.grade}</td>

                                                                                <td>{item.purpose ? item.purpose : item.settlement_purpose}</td>
                                                                                <td>{new Date(item.from_date).toLocaleDateString("en-GB").replace(/\//g, "-")}</td>
                                                                                <td>{new Date(item.to_date).toLocaleDateString("en-GB").replace(/\//g, "-")}</td>
                                                                                <td>
                                                                                    {
                                                                                        Math.trunc(Number(item.grandTotalAmount_user || 0)) === 0
                                                                                            ? Math.trunc(Number(item.TotalAmount || 0))
                                                                                            : Math.trunc(Number(item.grandTotalAmount_user))
                                                                                    }
                                                                                </td>
                                                                                <td>
                                                                                    {Math.trunc(item.audit_approved_amount)}
                                                                                </td>
                                                                                <td>
                                                                                    {item.audit_approved_date}
                                                                                </td>
                                                                                {/* <td>
                                                                                                                                                   {item.finance_approved_amount != 0
                                                                                                                                                       ? (item.Group === 'Y'
                                                                                                                                                           ? `${item.finance_approved_amount} (shared)`
                                                                                                                                                           : Math.trunc(item.finance_approved_amount))
                                                                                                                                                       : "Need to approval by finance"}
                                                                                                                                               </td> */}
                                                                                {/* <td>{item.report_mngr}</td> */}
                                                                                <td>{item.days}</td>


                                                                                <td style={{
                                                                                    color: item.rpt_mgr_status === 'Pending' ? 'orange' :
                                                                                        item.rpt_mgr_status === 'Rejected' ? 'red' :
                                                                                            item.rpt_mgr_status === 'Approved' ? 'green' :
                                                                                                'inherit'
                                                                                }}>
                                                                                    {item.rpt_mgr_status}
                                                                                </td>
                                                                                <td style={{
                                                                                    color: item.audit_status === 'Pending' ? 'orange' :
                                                                                        item.audit_status === 'Rejected' ? 'red' :
                                                                                            item.audit_status === 'Approved' ? 'green' :
                                                                                                'inherit'
                                                                                }}>{item.audit_status}</td>
                                                                                <td style={{ color: item.account_status === 'Pending' ? 'orange' : item.account_status === 'Rejected' ? 'red' : item.account_status === 'Approved' ? 'green' : 'inherit' }}>
                                                                                    {item.account_status}
                                                                                </td>



                                                                                {/* <td>
                                                                                    {item.ExStation === 'Y' ? (
                                                                                        <Link to={`/edit_audit_exstation_form/${item.id}`}>
                                                                                            <i className="fa fa-edit" style={{ fontSize: '12px', color: 'green', marginRight: '8px' }}></i>
                                                                                        </Link>
                                                                                    ) : item.Group === 'Y' ? (
                                                                                        <Link to={`/edit_audit_form_group/${item.id}`}>
                                                                                            <i className="fa fa-edit" style={{ fontSize: '12px', color: 'green', marginRight: '8px' }}></i>
                                                                                        </Link>
                                                                                    ) : (
                                                                                        <Link to={`/edit_audit_form/${item.id}`}>
                                                                                            <i className="fa fa-edit" style={{ fontSize: '12px', color: 'green', marginRight: '8px' }}></i>
                                                                                        </Link>
                                                                                    )}
                                                                                    <i
                                                                                        className="fa fa-eye"
                                                                                        onClick={() => handleLog(item.id)}
                                                                                        style={{ fontSize: '12px', color: 'green', cursor: 'pointer' }}
                                                                                    ></i>
                                                                                </td> */}
                                                                            </tr>
                                                                            // Skip rendering if the conditions are not met
                                                                        ))
                                                                    ) : (
                                                                        <tr>
                                                                            <td colSpan="15" style={{ textAlign: 'center' }}>No data available</td>
                                                                        </tr>
                                                                    )
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan="15" style={{ textAlign: 'center' }}>No data available</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                            <Pagination
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                handlePageChange={handlePageChange}
                                            />
                                            <div className="mb-3">
                                                Showing {startItem} to {endItem} of {filteredItems.length} entries
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            </section>
                        </>
                    )}
                    {showLogPopup && (() => {

                        const formData = logResponse?.data?.form || {};
                        const details = logResponse?.data?.details || [];

                        /* ================= SAFE SUM HELPER ================= */
                        const sum = (key) =>
                            details.reduce((t, i) => t + Number(i[key] || 0), 0);

                        /* ================= USER VALUES ================= */
                        const userHotel = sum("hotel_amount_user");
                        const userTicket = sum("ticket_travel_amount_user");
                        const userPerDiem = sum("perDiem_user");
                        const userLocal = sum("local_user");
                        const userMisc = sum("misc_user");

                        /* ================= SYSTEM VALUES ================= */
                        const sysHotel = sum("hotel_amount");
                        const sysTicket = sum("ticket_travel_amount");
                        const sysPerDiem = sum("perDiem");
                        const sysMisc = sum("misc");
                        const sysLocal = Number(formData.grandTotalLocal || 0);

                        /* ================= FINAL USER CLAIMED DISPLAY ================= */
                        const userDisplay = {
                            hotel: userHotel > 0 ? userHotel : sysHotel,
                            ticket: userTicket > 0 ? userTicket : sysTicket,
                            perDiem: userPerDiem > 0 ? userPerDiem : sysPerDiem,
                            local: userLocal > 0 ? userLocal : sysLocal,
                            misc: userMisc > 0 ? userMisc : sysMisc,

                            total:
                                (userHotel > 0 ? userHotel : sysHotel) +
                                (userTicket > 0 ? userTicket : sysTicket) +
                                (userPerDiem > 0 ? userPerDiem : sysPerDiem) +
                                (userLocal > 0 ? userLocal : sysLocal) +
                                (userMisc > 0 ? userMisc : sysMisc)
                        };

                        return (
                            <div style={overlayStyle}>
                                <div style={{ ...modalStyle, maxWidth: "900px" }}>
                                    <h4 style={{ marginBottom: "15px" }}>
                                        Settlement Amount Summary
                                    </h4>

                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>

                                        {/* ================= USER CLAIMED ================= */}
                                        <div
                                            style={{
                                                border: "1px solid #cbd5e1",
                                                borderRadius: "10px",
                                                padding: "14px",
                                                background: "#f8fafc"
                                            }}
                                        >
                                            <h5 style={{ marginBottom: "10px" }}>💼 User Claimed</h5>

                                            <table style={{ width: "100%", fontSize: "14px" }}>
                                                <tbody>
                                                    <tr>
                                                        <td>Employee</td>
                                                        <td align="right">{formData.comp_name}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Hotel Amount</td>
                                                        <td align="right">₹ {userDisplay.hotel}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Ticket Amount</td>
                                                        <td align="right">₹ {userDisplay.ticket}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Per Diem</td>
                                                        <td align="right">₹ {userDisplay.perDiem}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Local Conveyance</td>
                                                        <td align="right">₹ {userDisplay.local}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Misc</td>
                                                        <td align="right">₹ {userDisplay.misc}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><b>Total Claimed</b></td>
                                                        <td align="right">
                                                            <b>₹ {formData.grandTotalAmount || userDisplay.total}</b>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* ================= AUDIT APPROVED ================= */}
                                        <div
                                            style={{
                                                border: "1px solid #bbf7d0",
                                                borderRadius: "10px",
                                                padding: "14px",
                                                background: "#ecfdf5"
                                            }}
                                        >
                                            {formData?.audit_status === "Approved" ? (
                                                <>
                                                    <h5 style={{ marginBottom: "10px" }}>✅ Audit Approved</h5>

                                                    <table style={{ width: "100%", fontSize: "14px" }}>
                                                        <tbody>
                                                            <tr>
                                                                <td>Audit Status</td>
                                                                <td align="right"><b>{formData.audit_status}</b></td>
                                                            </tr>
                                                            <tr>
                                                                <td>Hotel Amount</td>
                                                                <td align="right">₹ {sysHotel}</td>
                                                            </tr>
                                                            <tr>
                                                                <td>Ticket Amount</td>
                                                                <td align="right">₹ {sysTicket}</td>
                                                            </tr>
                                                            <tr>
                                                                <td>Per Diem</td>
                                                                <td align="right">₹ {sysPerDiem}</td>
                                                            </tr>
                                                            <tr>
                                                                <td>Local Conveyance</td>
                                                                <td align="right">₹ {sysLocal}</td>
                                                            </tr>
                                                            <tr>
                                                                <td>Misc</td>
                                                                <td align="right">₹ {sysMisc}</td>
                                                            </tr>
                                                            <tr>
                                                                <td><b>Audit Approved</b></td>
                                                                <td align="right">
                                                                    <b style={{ color: "#047857" }}>
                                                                        ₹ {formData.audit_approved_amount || formData.grandTotalAmount}
                                                                    </b>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </>
                                            ) : (
                                                <h5 style={{ marginBottom: "10px", color: "#92400e" }}>
                                                    ⏳ Audit Not Approved
                                                </h5>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ textAlign: "right", marginTop: "16px" }}>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => setShowLogPopup(false)}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}


                </div>

                <Footer />
            </div>
        </div>
    );
};

export default AuditForm;
